import { fetchWithToken } from '@/services'
import fetcher from '@/services/fetcher'
import { buildQuery } from '@/utils/helpers'
import { IListPromotion, IListPromotionItem } from '../types'
const APPLY_COUPON = `
mutation applyCouponToCart($cart_id: String!, $coupon_code: String!) {
  applyCouponToCart(input: { cart_id: $cart_id, coupon_code: $coupon_code }) {
    cart {
      items {
        product {
          sku
          name
        }
        quantity
      }
      applied_coupons {
        code
      }
      prices {
        grand_total {
          value
          currency
        }
      }
    }
  }
}`

const REMOVE_COUPON = `
mutation removeCouponFromCart($input: RemoveCouponFromCartInput) {
   removeCouponFromCart(input: $input) {
  cart {
     id
   is_virtual
   total_quantity
 }
 }
  }`

const LIST_PROMOTION = `
query getCartPriceRule {
  cartPriceRule {
    items {
      code
      currency
      description
      discount_amount
      from_date
      is_active
      name
      simple_action
      to_date
      url_key
    }
  }
}`
const LIST_PROMOTION_V2 = `
query getCartPriceRule($cartId: String!) {
  cartPriceRule(cart_id: $cartId) {
    items {
      rule_id
      name
      description
      is_active
      discount_amount
      simple_action
      coupon_type
      from_date
      to_date
      currency
      code
      url_key
      customer_condition
      is_applied
      valid_coupons
      used_coupons
    }
  }
}`

// sku simple bất kỳ trong variant
// qty là số lượng của ng dùng nhập ?? 1
// voucher là mã coupon ng dùng nhập
const LIST_PROMOTION_V3 = `query getCartPriceRule($cartId: String!, $sku: String, $qty: Int, $voucher: String) {
  cartPriceRule(cart_id: $cartId, sku: $sku, qty: $qty, voucher: $voucher) {
    total_count
    grand_total
    single_price
    items {
     rule_id
      name
      description
      is_active
      discount_amount
      simple_action
      coupon_type
      from_date
      to_date
      currency
      code
      url_key
      customer_condition
      is_applied
      valid_coupons
      used_coupons
    }
  }
}`

const LIST_PROMOTION_V3_WITHOUT_ITEMS = `query getCartPriceRule($cartId: String!, $sku: String, $qty: Int, $voucher: String) {
  cartPriceRule(cart_id: $cartId, sku: $sku, qty: $qty, voucher: $voucher) {
    total_count
    grand_total
    single_price
  }
}`

const LIST_PROMOTION_V3_WITHOUT_CART = `query getCartPriceRule($sku: String, $qty: Int, $voucher: String) {
  cartPriceRule(sku: $sku, qty: $qty, voucher: $voucher) {
    total_count
    grand_total
    single_price
    items {
     rule_id
      name
      description
      is_active
      discount_amount
      simple_action
      coupon_type
      from_date
      to_date
      currency
      code
      url_key
      customer_condition
      is_applied
      valid_coupons
      used_coupons
    }
  }
}`

const applyCoupon = async (cartId: string, coupon_code: string) => {
  try {
    const res = await fetchWithToken({
      query: APPLY_COUPON,
      variables: {
        cart_id: cartId,
        coupon_code: coupon_code,
      },
    }).then((res) => res?.payload)
    if (res?.errors) {
      const err = res?.errors[0]?.debugMessage || res?.errors[0]?.message
      return { status: 'error', message: err, data: null }
    }
    const data = res?.data?.applyCouponToCart as IListPromotionItem
    if (!data) return { data: null, message: res?.errors?.[0]?.message, status: 'error' }
    return { data: data, status: 'success', message: '' }
  } catch (err) {
    console.log('Error when applying items to cart: ', err)
    return { data: null, status: 'error', message: JSON.stringify(err) }
  }
}

const removeCoupon = async (cartId: string) => {
  try {
    const res = await fetchWithToken({
      query: REMOVE_COUPON,
      variables: {
        input: {
          cart_id: cartId,
        },
      },
    }).then((res) => res?.payload)
    if (res?.errors) {
      const err = res?.errors[0]?.debugMessage || res?.errors[0]?.message
      return { status: 'error', message: err, data: null }
    }
    const data = res?.data?.removeCouponFromCart
    if (!data) return { data: null, message: res?.errors?.[0]?.message, status: 'error' }
    return { data: data, status: 'success', message: `` }
  } catch (err) {
    console.log('Error when applying items to cart: ', err)
    return { data: null, status: 'error', message: JSON.stringify(err) }
  }
}

const getListPromotion = async ({
  cartId,
  sku,
  qty,
  voucher,
}: {
  cartId: string
  sku: string
  qty: number
  voucher: string
}) => {
  try {
    if (!cartId) {
      return { status: 'error', message: 'Cart ID is required', data: null }
    }

    const response = await fetchWithToken(
      {
        query: LIST_PROMOTION_V3,
        variables: { cartId: cartId, sku, qty, voucher },
      },
      {
        next: { revalidate: 120, tags: [`list-promotion`] },
      }
    )

    const data = response?.payload?.data?.cartPriceRule as IListPromotion
    if (response?.payload?.errors) {
      return { status: 'error', message: response.payload.errors[0]?.message, data: null }
    }

    return { status: 'success', data, message: '' }
  } catch (error) {
    console.error('Error fetching list promotion:', error)
    return { status: 'error', message: 'Có lỗi xảy ra khi tải danh sách khuyến mãi', data: null }
  }
}

const getListPromotionV2 = async ({ cartId }: { cartId: string }) => {
  try {
    if (!cartId) {
      return { status: 'error', message: 'Cart ID is required', data: null }
    }

    const response = await fetchWithToken(
      {
        query: LIST_PROMOTION_V2,
        variables: { cartId: cartId },
      },
      {
        next: { revalidate: 120, tags: ['list-promotion', `list-promotion-v2`] },
      }
    )

    const data = response?.payload?.data?.cartPriceRule as Omit<
      IListPromotion,
      'total_count' | 'grand_total' | 'single_price'
    >
    if (response?.payload?.errors) {
      return { status: 'error', message: response.payload.errors[0]?.message, data: null }
    }

    return { status: 'success', data, message: '' }
  } catch (error) {
    console.error('Error fetching list promotion:', error)
    return { status: 'error', message: 'Có lỗi xảy ra khi tải danh sách khuyến mãi', data: null }
  }
}

const getPricePromotion = async ({
  cartId,
  sku,
  qty,
  voucher,
}: {
  cartId: string
  sku: string
  qty: number
  voucher: string
}) => {
  try {
    if (!cartId) {
      return { status: 'error', message: 'Cart ID is required', data: null }
    }

    const response = await fetchWithToken(
      {
        query: LIST_PROMOTION_V3_WITHOUT_ITEMS,
        variables: { cartId: cartId, sku, qty, voucher },
      },
      {
        next: { revalidate: 120, tags: [`list-promotion`, 'list-promotion-without-items'] },
      }
    )

    const data = response?.payload?.data?.cartPriceRule as IListPromotion
    if (response?.payload?.errors) {
      return { status: 'error', message: response.payload.errors[0]?.message, data: null }
    }

    return { status: 'success', data, message: '' }
  } catch (error) {
    console.error('Error fetching list promotion:', error)
    return { status: 'error', message: 'Có lỗi xảy ra khi tải danh sách khuyến mãi', data: null }
  }
}
const getListPromotionWithoutCart = async ({
  sku,
  qty,
  voucher,
}: {
  sku: string
  qty: number
  voucher: string
}) => {
  try {
    const response = await fetchWithToken(
      {
        query: LIST_PROMOTION_V3_WITHOUT_CART,
        variables: { sku, qty, voucher },
      },
      {
        next: { revalidate: 120, tags: [`list-promotion`, 'list-promotion-without-cart'] },
      }
    )

    const data = response?.payload?.data?.cartPriceRule as IListPromotion
    if (response?.payload?.errors) {
      return { status: 'error', message: response.payload.errors[0]?.message, data: null }
    }

    return { status: 'success', data, message: '' }
  } catch (error) {
    console.error('Error fetching list promotion:', error)
    return { status: 'error', message: 'Có lỗi xảy ra khi tải danh sách khuyến mãi', data: null }
  }
}

export { applyCoupon, removeCoupon, getListPromotion, getListPromotionV2, getPricePromotion, getListPromotionWithoutCart }
